import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { contentService } from '../../services/content';
import { Beat } from '../../types/content';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import './BeatCard.css';

interface Props {
  beat: Beat;
  onVote?: () => void;
  /** Surface the card sits on. Dark glass for the feed pages, white for the light panels. */
  variant?: 'light' | 'dark';
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

const BeatCard: React.FC<Props> = ({ beat, onVote, variant = 'light' }) => {
  const { authState } = useAuth();
  const {
    play,
    pause,
    isPlaying,
    stop,
    currentAudioKey,
    currentTime,
    duration,
    formatTime,
  } = useAudioPlayer();
  const [isLooping, setIsLooping] = useState(false);
  const [isVoting, setIsVoting] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const navigate = useNavigate();

  const isBeatPlaying = isPlaying && currentAudioKey === beat.id;

  useEffect(() => {
    // beat.file_url is already the full public URL.
    if (beat.file_url) {
      setAudioUrl(beat.file_url);
    } else {
      setAudioError('No audio file available for this beat.');
    }
  }, [beat.file_url]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleLoadedMetadata = () => setAudioDuration(audio.duration);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
  }, [audioUrl]);

  const handlePlayPause = () => {
    if (!audioUrl || !audioRef.current) {
      setAudioError('Audio file not available');
      return;
    }
    if (isBeatPlaying) {
      pause();
      return;
    }
    setAudioError(null);
    play(audioRef.current, beat.id).catch(() => setAudioError('Unable to play audio file'));
  };

  const handleAudioError = () => {
    setAudioError('Error loading audio file');
    stop();
  };

  const toggleLoop = () => {
    if (!audioRef.current) return;
    audioRef.current.loop = !isLooping;
    setIsLooping(!isLooping);
  };

  const handleVote = async (value: 1 | -1) => {
    if (!authState.user) return;
    setIsVoting(true);
    try {
      if (value === 1) {
        await contentService.vote('beat', beat.id, 1);
      } else if (value === -1 && beat.user_vote === 1) {
        await contentService.deleteVote('beat', beat.id);
      }
      if (onVote) onVote();
    } catch (err) {
      console.error('Error voting:', err);
    } finally {
      setIsVoting(false);
    }
  };

  const openBeat = (e: React.MouseEvent | React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('input')) return;
    navigate(`/beats/${beat.id}`);
  };

  const progress = isBeatPlaying && duration > 0 ? (currentTime / duration) * 100 : 0;
  const isListener = authState.user?.role === 'listener';
  const isCollab = beat.beat_type === 'collaboration';

  return (
    <article
      className={`beat-card beat-card--${variant}`}
      onClick={openBeat}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openBeat(e);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Open ${beat.title}`}
    >
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="metadata"
          onEnded={() => stop()}
          onError={handleAudioError}
        />
      )}

      <header className="beat-card__head">
        <span className="beat-card__badge" aria-hidden>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 18V5l10-2v13M9 18a3 3 0 11-6 0 3 3 0 016 0zm10-2a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </span>
        <div className="beat-card__identity">
          <h3 className="beat-card__title">{beat.title}</h3>
          {beat.producer && (
            <p className="beat-card__by">
              by{' '}
              <Link to={`/profile/${beat.producer.id}`} className="beat-card__by-link">
                {beat.producer.username}
              </Link>
            </p>
          )}
        </div>
        <time className="beat-card__date" dateTime={beat.created_at}>
          {formatDate(beat.created_at)}
        </time>
      </header>

      <div className="beat-card__chips">
        <span className="beat-card__chip beat-card__chip--genre">{beat.genre}</span>
        <span className="beat-card__chip beat-card__chip--bpm">{beat.bpm} BPM</span>
        <span
          className={`beat-card__chip ${
            isCollab ? 'beat-card__chip--collab' : 'beat-card__chip--cypher'
          }`}
        >
          {isCollab ? 'Collab' : 'Cypher'}
        </span>
      </div>

      <div className="beat-card__player">
        <button
          type="button"
          onClick={handlePlayPause}
          disabled={!audioUrl || !!audioError}
          className="beat-card__play"
          aria-label={isBeatPlaying ? 'Pause beat' : 'Play beat'}
          title={audioError || (!audioUrl ? 'Audio not available' : 'Play / pause')}
        >
          {isBeatPlaying ? (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M8 5h3v14H8zM13 5h3v14h-3z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M8 5.14v13.72L19 12z" />
            </svg>
          )}
        </button>

        <div className="beat-card__track">
          <div className="beat-card__bar">
            <span className="beat-card__bar-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="beat-card__times">
            <span>{isBeatPlaying ? formatTime(currentTime) : '0:00'}</span>
            <span>
              {isBeatPlaying
                ? formatTime(duration)
                : audioDuration > 0
                  ? formatTime(audioDuration)
                  : '--:--'}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={toggleLoop}
          disabled={!audioUrl || !!audioError}
          className={`beat-card__loop ${isLooping ? 'beat-card__loop--on' : ''}`}
          aria-pressed={isLooping}
          aria-label={isLooping ? 'Disable loop' : 'Enable loop'}
          title={isLooping ? 'Looping' : 'Loop this beat'}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>

      {audioError && <p className="beat-card__error">{audioError}</p>}

      <footer className="beat-card__footer">
        {isListener ? (
          <button
            type="button"
            onClick={() => handleVote(beat.user_vote === 1 ? -1 : 1)}
            disabled={isVoting}
            className={`beat-card__vote ${beat.user_vote === 1 ? 'beat-card__vote--on' : ''}`}
            aria-label={beat.user_vote === 1 ? 'Remove vote' : 'Vote'}
          >
            <svg
              viewBox="0 0 24 24"
              fill={beat.user_vote === 1 ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z" />
            </svg>
            <span>{beat.vote_count || 0}</span>
          </button>
        ) : (
          <span className="beat-card__likes">
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            {beat.vote_count || 0}
          </span>
        )}

        <span className="beat-card__open" aria-hidden>
          View beat
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </footer>
    </article>
  );
};

export default BeatCard;
